import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { stringify } from '../../../../util/urlHelpers';
import { FormattedMessage, injectIntl } from '../../../../util/reactIntl';
import { NamedLink, IconSpinner } from '../../../../components';
import { redirectToURLWithModalState } from '../../../../components/Topbar/Topbar.helpers';
import { TopbarLocationSearchForm } from '../../../../forms';
import { useCurrentUserGeoData } from '../../../../hooks/useCurrentUserGeoData';
import {
    encodeBoundsToStr,
    encodeOriginToStr,
} from '../../../../components/Topbar/components/TopbarDesktop/TopbarDesktopSearchPanel.helpers';
import config from '../../../../config';
import TopbarDesktopSearchPanel from '../../../../components/Topbar/components/TopbarDesktop/TopbarDesktopSearchPanel';
import SectionHeroHeader from './SectionHeroHeader';
import css from './SectionHero.css';

const { listingTypeHorse, userTypeRider, userTypeHorseowner, listingTypeRider } = config;

const userToListingTypeMapper = {
    [userTypeRider]: listingTypeHorse,
    [userTypeHorseowner]: listingTypeRider,
};

const SectionHero = ({
    intl,
    userType,
    type,
    computing,
    isMobile,
    location,
    history,
    currentUser,
    hideSearhBarForNoAuth = false,
}) => {
    const [initialSearchPanelValues, setInitialSearchPanelValues] = useState(null);

    const initializeRedirectParams = () => {
        if (
            !initialSearchPanelValues ||
            !initialSearchPanelValues.location ||
            !initialSearchPanelValues.location.selectedPlace
        )
            return {};

        return {
            search: stringify({
                address: initialSearchPanelValues.location.search,
                bounds: encodeBoundsToStr(initialSearchPanelValues.location.selectedPlace.bounds),
                origin: encodeOriginToStr(initialSearchPanelValues.location.selectedPlace.origin),
                distance: initialSearchPanelValues.optionDistance,
                listingType,
            }),
        };
    };

    const currentUserDefined = !!userType && currentUser && currentUser.id;
    const isHorseOwner = userType === userTypeHorseowner;

    const horseownerActionLink = !isHorseOwner ? null : (
        <p className={css.horseownerActionLink}>
            <FormattedMessage
                id="SectionHero.createYourListingForFree"
                values={{
                    createListingLink: (
                        <NamedLink name="NewListingPage">
                            <FormattedMessage id="SectionHero.createListingLink" />
                        </NamedLink>
                    ),
                }}
            />
        </p>
    );

    const { loading, geoData: currentLocation } = useCurrentUserGeoData({
        allowed: true,
    });

    const inProgress = computing || loading;
    const showSearchBar = !inProgress && (hideSearhBarForNoAuth ? currentUserDefined : true);

    const listingType = type || userToListingTypeMapper[userType] || 'horse';

    const { city, postalCode, mobility } = currentUser.attributes.profile.publicData;
    const { radius } = mobility || { radius: '15 km' };
    const { address } = currentUserDefined
        ? {
              address: `${postalCode} ${city}`,
          }
        : {};

    useEffect(() => {
        if (!currentLocation) return;

        setInitialSearchPanelValues({
            optionDistance: radius,
            location: {
                search: currentLocation.entityLocationSearchString || address,
                selectedPlace: { ...currentLocation },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation]);

    return (
        <section className={css.root}>
            <div className={css.container}>
                <SectionHeroHeader userType={userType} />

                {inProgress && <IconSpinner />}

                {isMobile && showSearchBar && (
                    <>
                        <TopbarLocationSearchForm
                            rootClassName={css.mobLocationForm}
                            listingTypeTab={isHorseOwner ? listingTypeRider : listingTypeHorse}
                            initialValues={
                                initialSearchPanelValues && {
                                    distance: initialSearchPanelValues.optionDistance,
                                    address: initialSearchPanelValues.location.search,
                                }
                            }
                            onTabPanelToggle={() =>
                                redirectToURLWithModalState(
                                    {
                                        location: {
                                            ...location,
                                            ...initializeRedirectParams(),
                                        },
                                        history,
                                    },
                                    'mobilesearch'
                                )
                            }
                        />

                        {horseownerActionLink}
                    </>
                )}
                {!isMobile && showSearchBar && (
                    <div>
                        <TopbarDesktopSearchPanel
                            intl={intl}
                            listingTypeTab={isHorseOwner ? listingTypeRider : listingTypeHorse}
                            rootClassName={css.sectionHeroSearchPanel}
                            initialValues={initialSearchPanelValues}
                        />
                        {horseownerActionLink}
                    </div>
                )}
            </div>
        </section>
    );
};

export default compose(withRouter, injectIntl)(SectionHero);
